/**
 * PKCE helpers for APS 3-legged OAuth in the browser (no client secret).
 * @see https://aps.autodesk.com/en/docs/oauth/v2/tutorials/get-3-legged-token-pkce/
 */

const VERIFIER_KEY = "vera_pkce_verifier";
const STATE_KEY = "vera_pkce_state";

function randomUrlSafe(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export async function createPkcePair(): Promise<{
  verifier: string;
  challenge: string;
}> {
  const verifier = randomUrlSafe(48);
  const challenge = await sha256Base64Url(verifier);
  return { verifier, challenge };
}

export function stashPkceSession(verifier: string, state: string): void {
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
}

export function takePkceVerifier(expectedState: string | null): string {
  const state = sessionStorage.getItem(STATE_KEY);
  if (expectedState && state !== expectedState) {
    throw new Error(
      "OAuth state mismatch — possible CSRF; try signing in again."
    );
  }
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  if (!verifier) {
    throw new Error("Missing PKCE verifier — start sign-in from Vera again.");
  }
  return verifier;
}

export async function buildAuthorizationUrl(params: {
  authorizeUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  /** OAuth prompt — Autodesk allows only one of: `login`, `none`, `create`. */
  prompt?: "login" | "none" | "create";
}): Promise<string> {
  const { verifier, challenge } = await createPkcePair();
  const state = randomUrlSafe(16);
  stashPkceSession(verifier, state);

  const q = new URLSearchParams({
    response_type: "code",
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    scope: params.scope,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });
  if (params.prompt) q.set("prompt", params.prompt);
  return `${params.authorizeUrl}?${q.toString()}`;
}

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  /** Present when `openid` scope was granted — richest source for name/email/picture. */
  id_token?: string;
  scope?: string;
}

export async function exchangeCodeForToken(params: {
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  code: string;
  codeVerifier: string;
}): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: params.clientId,
    code: params.code,
    redirect_uri: params.redirectUri,
    code_verifier: params.codeVerifier,
  });

  return postToken(params.tokenUrl, body);
}

/** Renew an access token using a refresh token (PKCE public client — no secret). */
export async function refreshAccessToken(params: {
  tokenUrl: string;
  clientId: string;
  refreshToken: string;
}): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: params.clientId,
    refresh_token: params.refreshToken,
  });

  return postToken(params.tokenUrl, body);
}

async function postToken(
  tokenUrl: string,
  body: URLSearchParams
): Promise<TokenResponse> {
  const res = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token request failed (${res.status}): ${text}`);
  }

  return (await res.json()) as TokenResponse;
}
